import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, PointerEvent as ReactPointerEvent } from "react";
import { errorMessage } from "../../core/support";
import { format } from "../../i18n/labels";
import { useLabels, useMapCore } from "../hooks";
import { Button } from "../primitives/Button";
import { Modal } from "../primitives/Modal";

export type PhotoFieldProps = {
  fieldKey: string;
  label: string;
  ids: string[];
  editable: boolean;
  onChange: (ids: string[]) => void;
};

export const swipeMinPx = 40;

export function swipeStep(dx: number, dy: number): -1 | 0 | 1 {
  if (Math.abs(dx) < swipeMinPx || Math.abs(dx) <= Math.abs(dy)) return 0;

  return dx < 0 ? 1 : -1;
}

export function PhotoField({ fieldKey, label, ids, editable, onChange }: PhotoFieldProps) {
  const core = useMapCore();
  const labels = useLabels();
  const inputRef = useRef<HTMLInputElement>(null);
  const pointers = useRef(new Set<number>());
  const swipe = useRef<{ id: number; x: number; y: number } | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const count = ids.length;
  const viewing = open !== null;

  const step = useCallback((delta: number) => setOpen(current => (current === null ? null : Math.min(count - 1, Math.max(0, current + delta)))), [count]);

  useEffect(() => {
    if (!viewing) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      event.preventDefault();
      event.stopPropagation();
      step(event.key === "ArrowLeft" ? -1 : 1);
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [viewing, step]);

  useEffect(() => {
    if (viewing) return;

    pointers.current.clear();
    swipe.current = null;
  }, [viewing]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.add(event.pointerId);
    swipe.current = event.pointerType !== "mouse" && pointers.current.size === 1 ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = swipe.current;

    pointers.current.delete(event.pointerId);
    swipe.current = null;

    if (start === null || start.id !== event.pointerId) return;

    const delta = swipeStep(event.clientX - start.x, event.clientY - start.y);

    if (delta !== 0) step(delta);
  };

  const onPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    swipe.current = null;
  };

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = "";

    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploaded = await core.api.uploadFiles(files, true);

      onChange([...ids, ...uploaded.map(a => a.id)]);
    } catch (error) {
      core.store.getState().pushToast({ severity: "danger", text: `${labels.uploadFailed}: ${errorMessage(error, labels)}` });
    } finally {
      setUploading(false);
    }
  };

  const current = open === null ? null : (ids[open] ?? null);
  const position = (index: number) => format(labels.photoOf, { index: index + 1, count: ids.length });

  return (
    <div className="fxmap-photo-field" data-fxmap="photo-field" data-key={fieldKey}>
      {ids.length === 0 && !editable ? <span className="fxmap-field-text fxmap-muted">{labels.noPicture}</span> : null}
      <div className="fxmap-photo-grid">
        {ids.map((id, index) => (
          <figure key={`${id}:${index}`} className="fxmap-photo">
            <button type="button" className="fxmap-photo-open" aria-label={position(index)} onClick={() => setOpen(index)}>
              <img src={core.api.fileUrl(id)} alt="" loading="lazy" />
            </button>
            {editable ? <Button kind="icon" size="sm" icon="trash" label={labels.removePhoto} className="fxmap-photo-remove" action="remove-photo" onClick={() => onChange(ids.filter((_, i) => i !== index))} /> : null}
          </figure>
        ))}
        {editable ? (
          <>
            <Button kind="ghost" size="sm" icon="plus" label={labels.addPhoto} className="fxmap-photo-add" action="add-photo" busy={uploading} onClick={() => inputRef.current?.click()} />
            <input ref={inputRef} type="file" accept="image/*" multiple hidden data-fxmap="photo-input" onChange={event => void upload(event)} />
          </>
        ) : null}
      </div>
      {current !== null && open !== null ? (
        <Modal
          open
          title={label}
          subtitle={position(open)}
          onClose={() => setOpen(null)}
          closeLabel={labels.close}
          width="min(1100px, calc(100% - 32px))"
          className="fxmap-lightbox"
          dataFxmap="photo-lightbox"
          headerTools={
            <>
              <Button kind="icon" size="sm" icon="chevronLeft" label={labels.previous} disabled={open === 0} onClick={() => setOpen(open - 1)} />
              <Button kind="icon" size="sm" icon="chevronRight" label={labels.next} disabled={open >= ids.length - 1} onClick={() => setOpen(open + 1)} />
            </>
          }
        >
          <div className="fxmap-lightbox-stage" data-fxmap="photo-stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>
            <img className="fxmap-lightbox-image" src={core.api.fileUrl(current)} alt={position(open)} draggable={false} />
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
